import React, { ReactElement } from 'react'
import { withRouter, Link, RouteComponentProps } from 'react-router-dom'
import { MetaTitle, LoadError } from '../../Components/UI'
import { useTranslation } from 'react-i18next'
import { Typography, Button, Alert } from 'antd'
import { s } from './style'
import { EnquiryDetails } from './EnquiryDetails'
import { ProposalCard } from './ProposalCard'
import { LeftOutlined } from '@ant-design/icons'
import { ROUTES } from '../../Constants'
import { useGlobalAuth } from '@headbox-ui/auth-hook'
import { useAllProposalsEventQuery } from './AllProposals.generated-types'
import { ProposalStatus } from '../../types'

const { Title, Text } = Typography

const AllProposals: React.FC<RouteComponentProps<{ eventUUID: string }>> = ({
  history,
  match: {
    params: { eventUUID },
  },
}) => {
  const { t } = useTranslation()
  const { authenticated } = useGlobalAuth()

  const [{ fetching, error, data }] = useAllProposalsEventQuery({ variables: { UUID: eventUUID } })

  if (error || fetching) return <LoadError load={fetching} error={error} />

  const event = data?.getEventByUUID
  const proposals = event?.proposals

  const goToProposal = (propsalUUID: string, inactive = false): void => {
    history.push({
      pathname: `${ROUTES.PROPOSAL}/${propsalUUID}`,
      state: { inactive },
    })
  }

  const NavControlls = (): ReactElement => (
    <section style={s.marg}>
      {authenticated && (
        <Link to={`${ROUTES.EVENT_HUB}`}>
          <LeftOutlined />
          <Button type='link' style={s.noPad}>
            <Text strong style={{ color: '#1270B8' }}>
              {t('allProposals.goToEvents', 'Back to Smart Book')}
            </Text>
          </Button>
        </Link>
      )}
      <Title style={s.title} level={2}>
        {t('allProposals.title', 'All responses from venues')}
      </Title>
      <Text style={s.subhead}>
        {t(
          'allProposals.subtitle',
          'Explore offers from venues and book the one that work best for you.'
        )}
      </Text>
    </section>
  )

  const ProposalsGallery = ({ inactive = false }): ReactElement => (
    <section style={s.gallery}>
      {proposals?.length > 0 ? (
        proposals
          .filter((proposal) => proposal.status === ProposalStatus.SentToGuest)
          .map((p, i) => (
            <ProposalCard
              proposal={p}
              key={i}
              goToProposal={() => goToProposal(p.UUID, inactive)}
            />
          ))
      ) : (
        <Alert
          style={{ width: '100%', margin: 8 }}
          showIcon
          type={'info'}
          message={t('allProposals.noProposals', 'No responses yet.')}
        />
      )}
    </section>
  )

  const Wrapper = ({ meta, children }): ReactElement => (
    <section style={s.main}>
      <MetaTitle title={meta} />
      <NavControlls />
      <EnquiryDetails event={event} />
      {children}
    </section>
  )

  if (event && event.status !== 'AwaitingDecision') {
    return (
      <Wrapper meta={`${t('allProposals.metaTitle.awaiting', 'Smart Book responses  - HeadBox')}`}>
        <ProposalsGallery inactive />
      </Wrapper>
    )
  }

  return (
    <Wrapper meta={`${t('allProposals.metaTitle.active', 'Smart Book responses - HeadBox')}`}>
      <ProposalsGallery />
    </Wrapper>
  )
}

export default withRouter(AllProposals)
